import {
  Box,
  ChoicePanel,
  Inset,
  LabelChipV2,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import { CspIcon } from '@cast/shared-csp-icons-ui';
import { K8sProviderIcon } from '@cast/features-cluster-ui';
import { Cloud, K8sProvider } from '@cast/shared-types';

import { ANYWHERE_PROVIDER, ScriptMethodProvider } from '../_types';

type ProviderSelectProps = {
  selected: ScriptMethodProvider;
  onSelect: (provider: ScriptMethodProvider) => void;
  disabledProviders?: ScriptMethodProvider[];
  comingSoonProviders?: ScriptMethodProvider[];
  showAnywhere?: boolean;
};

const CLOUD_PROVIDERS = [
  { value: Cloud.aws, label: 'AWS' },
  { value: Cloud.gcp, label: 'GCP' },
  { value: Cloud.azure, label: 'Azure' },
] as const satisfies ReadonlyArray<{
  value: Cloud;
  label: string;
}>;

export const ProviderSelect = ({
  selected,
  onSelect,
  disabledProviders = [],
  comingSoonProviders = [],
  showAnywhere = true,
}: ProviderSelectProps) => (
  <Stack direction="row" flexWrap="wrap" gap={12} width="100%">
    {CLOUD_PROVIDERS.map((provider) => {
      const isSelected = selected === provider.value;
      const isDisabled = disabledProviders.includes(provider.value);
      const isComingSoon = comingSoonProviders.includes(provider.value);

      return (
        <ChoicePanel
          key={provider.value}
          selected={isSelected}
          disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) {
              onSelect(provider.value);
            }
          }}
          sx={{
            flex: '1 1 96px',
            minWidth: 96,
            ...(isComingSoon && {
              backgroundColor: tokens.theme.disabled.bg,
              borderColor: 'transparent',
              pointerEvents: 'none',
            }),
          }}
        >
          <Inset
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 16,
            }}
          >
            <Stack gap={4} alignItems="center" sx={{ py: 8 }}>
              <Box sx={{ opacity: isComingSoon ? 0.5 : 1 }}>
                <CspIcon csp={provider.value} size={24} />
              </Box>
              <Typography
                variant="A1"
                sx={{
                  color: isComingSoon
                    ? tokens.theme.disabled.fg
                    : isSelected
                    ? tokens.theme.fg.selection.primary
                    : tokens.theme.fg.structural.secondary,
                  textAlign: 'center',
                }}
              >
                {provider.label}
              </Typography>
              {isComingSoon ? (
                <LabelChipV2
                  label="Coming soon"
                  color="indigo"
                  size="xs"
                  sx={{
                    flexShrink: 0,
                    width: 'max-content',
                    whiteSpace: 'nowrap',
                  }}
                />
              ) : null}
            </Stack>
          </Inset>
        </ChoicePanel>
      );
    })}
    {showAnywhere
      ? (() => {
          const isSelected = selected === ANYWHERE_PROVIDER;
          const isDisabled = disabledProviders.includes(ANYWHERE_PROVIDER);
          const isComingSoon = comingSoonProviders.includes(ANYWHERE_PROVIDER);

          return (
            <ChoicePanel
              selected={isSelected}
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  onSelect(ANYWHERE_PROVIDER);
                }
              }}
              sx={{
                flex: '1 1 96px',
                minWidth: 96,
                ...(isComingSoon && {
                  backgroundColor: tokens.theme.disabled.bg,
                  borderColor: 'transparent',
                  pointerEvents: 'none',
                }),
              }}
            >
              <Inset
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 16,
                }}
              >
                <Stack gap={4} alignItems="center" sx={{ py: 8 }}>
                  <Box sx={{ opacity: isComingSoon ? 0.5 : 1 }}>
                    <K8sProviderIcon
                      provider={K8sProvider.ANYWHERE}
                      size={24}
                    />
                  </Box>
                  <Typography
                    variant="A1"
                    sx={{
                      color: isComingSoon
                        ? tokens.theme.disabled.fg
                        : isSelected
                        ? tokens.theme.fg.selection.primary
                        : tokens.theme.fg.structural.secondary,
                      textAlign: 'center',
                    }}
                  >
                    Anywhere
                  </Typography>
                  {isComingSoon ? (
                    <LabelChipV2
                      label="Coming soon"
                      color="indigo"
                      size="xs"
                      sx={{
                        flexShrink: 0,
                        width: 'max-content',
                        whiteSpace: 'nowrap',
                      }}
                    />
                  ) : null}
                </Stack>
              </Inset>
            </ChoicePanel>
          );
        })()
      : null}
  </Stack>
);
